import React, { useState } from 'react';
import WorkspaceShell from './WorkspaceShell';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import {
  useGetCoBrokerAgreementsQuery,
  useCreateCoBrokerAgreementMutation,
  useRespondCoBrokerAgreementMutation,
  useUpdateCoBrokerStatusMutation,
} from '../../features/api/apiSlice';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCustomerId } from '../../features/auth/authSlice';

const CoBrokerPage = () => {
  const user = useSelector(selectCurrentUser);
  const myId = useSelector(selectCustomerId);
  const { data: agreements = [], isLoading, refetch } = useGetCoBrokerAgreementsQuery();
  const [createAgreement, { isLoading: creating }] = useCreateCoBrokerAgreementMutation();
  const [respond] = useRespondCoBrokerAgreementMutation();
  const [updateStatus] = useUpdateCoBrokerStatusMutation();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    partner_email: '',
    property_ref: '',
    buyer_side_label: 'I have the buyer',
    listing_side_label: 'Partner has the listing',
    initiator_split_percent: 50,
    partner_split_percent: 50,
    estimated_deal_value: '',
    commission_total_percent: 5,
  });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const onCreate = async (e) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    try {
      await createAgreement({
        ...form,
        estimated_deal_value: form.estimated_deal_value || null,
        initiator_signature: user?.name || 'Signed',
      }).unwrap();
      setMsg('Agreement sent to partner for acceptance.');
      setShowForm(false);
      setForm((f) => ({ ...f, title: '', description: '', partner_email: '', property_ref: '' }));
      refetch();
    } catch (e2) {
      setErr(e2?.data?.message || e2?.message || 'Failed to create agreement');
    }
  };

  const onRespond = async (id, action) => {
    try {
      await respond({ id, action, signature: user?.name || 'Signed' }).unwrap();
      refetch();
    } catch (e2) {
      setErr(e2?.data?.message || 'Action failed');
    }
  };

  return (
    <WorkspaceShell
      title="Co-Brokering Network"
      subtitle="Agent-to-agent collaboration: split commissions and sign digital co-broker agreements on-platform."
    >
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">Invite a partner by email. Both parties e-sign to activate.</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'New agreement'}
        </Button>
      </div>

      {msg && <p className="text-success text-sm mb-3">{msg}</p>}
      {err && <p className="text-error text-sm mb-3">{err}</p>}

      {showForm && (
        <form onSubmit={onCreate} className="mb-8 rounded-xl border border-border bg-card p-5 space-y-3">
          <Input label="Deal title" value={form.title} required
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Kololo apartment — buyer intro" />
          <Input label="Partner agent email" type="email" value={form.partner_email}
            onChange={(e) => setForm({ ...form, partner_email: e.target.value })}
            placeholder="partner@agency.com" />
          <Input label="Property reference" value={form.property_ref}
            onChange={(e) => setForm({ ...form, property_ref: e.target.value })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Your side" value={form.buyer_side_label}
              onChange={(e) => setForm({ ...form, buyer_side_label: e.target.value })} />
            <Input label="Partner side" value={form.listing_side_label}
              onChange={(e) => setForm({ ...form, listing_side_label: e.target.value })} />
            <Input label="Your split %" type="number" value={form.initiator_split_percent}
              onChange={(e) => setForm({ ...form, initiator_split_percent: Number(e.target.value) })} />
            <Input label="Partner split %" type="number" value={form.partner_split_percent}
              onChange={(e) => setForm({ ...form, partner_split_percent: Number(e.target.value) })} />
            <Input label="Est. deal value (UGX)" type="number" value={form.estimated_deal_value}
              onChange={(e) => setForm({ ...form, estimated_deal_value: e.target.value })} />
            <Input label="Total commission %" type="number" value={form.commission_total_percent}
              onChange={(e) => setForm({ ...form, commission_total_percent: Number(e.target.value) })} />
          </div>
          <textarea
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm min-h-[80px]"
            placeholder="Notes / deal description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <Button type="submit" loading={creating} disabled={creating}>Create & send for signature</Button>
        </form>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Loading agreements…</p>
      ) : agreements.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          No co-broker agreements yet. Create one when you have a buyer and another agent has the listing (or vice versa).
        </div>
      ) : (
        <div className="space-y-3">
          {agreements.map((a) => {
            const iAmPartner = String(a.partner_id) === String(myId)
              || (a.partner_email && user?.email && a.partner_email.toLowerCase() === user.email.toLowerCase());
            const canRespond = a.status === 'pending_partner' && iAmPartner;
            return (
              <div key={a.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <h3 className="font-semibold">{a.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {a.initiator?.name || 'Initiator'} ↔ {a.partner?.name || a.partner_email || 'Partner pending'}
                      {a.property_ref ? ` · ${a.property_ref}` : ''}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-muted capitalize h-fit">{a.status?.replace('_', ' ')}</span>
                </div>
                <p className="text-sm mt-2">
                  Split: {a.initiator_split_percent}% / {a.partner_split_percent}%
                  {a.estimated_deal_value != null && ` · Deal ~ ${a.currency} ${Number(a.estimated_deal_value).toLocaleString()}`}
                </p>
                {a.description && <p className="text-sm text-muted-foreground mt-1">{a.description}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                  {canRespond && (
                    <>
                      <Button size="sm" onClick={() => onRespond(a.id, 'accept')}>Accept & sign</Button>
                      <Button size="sm" variant="outline" onClick={() => onRespond(a.id, 'decline')}>Decline</Button>
                    </>
                  )}
                  {a.status === 'active' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => updateStatus({ id: a.id, status: 'completed' })}>
                        Mark completed
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus({ id: a.id, status: 'cancelled' })}>
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WorkspaceShell>
  );
};

export default CoBrokerPage;
